import us25Archief from "../data/us25-meldingen-archief.json";

interface Props {
  onBack: () => void;
}

interface Melding {
  tijd: string;
  melder: string | null;
  bericht: string;
  uitgezet_naar: string | null;
  afhandeling: string | null;
}

interface InstroompuntMeta {
  tijd: string;
  count: number;
  toelichting?: string;
}

const archief = us25Archief as {
  meta: { herkomst: string };
  datum: string;
  evenement: string;
  opdrachtgever: string;
  locatie: string;
  cp_team: { naam: string; functie: string; afkorting: string }[];
  noot_afkortingen: string;
  instroom_reeks: InstroompuntMeta[];
  tijd_in: string;
  tijd_uit: string;
  meldingen: Melding[];
};

export function US25ArchiefView({ onBack }: Props) {
  const total = archief.meldingen.length;
  const tijdspan = `${archief.tijd_in} — ${archief.tijd_uit}`;

  return (
    <div className="min-h-full safe-top safe-bottom flex flex-col">
      <header className="px-5 pt-6 pb-4 flex items-center justify-between">
        <button
          onClick={onBack}
          aria-label="Terug naar overzicht"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-zinc-200 text-2xs uppercase font-semibold"
          style={{ letterSpacing: "0.18em" }}
        >
          <span aria-hidden="true">←</span>
          Overzicht
        </button>
        <div className="flex items-center gap-2.5">
          <span className="w-1 h-5 bg-zinc-600 rounded-full" />
          <span
            className="text-zinc-500 text-[10px] font-semibold uppercase"
            style={{ letterSpacing: "0.2em" }}
          >
            US25 · ARCHIEF
          </span>
        </div>
      </header>

      <main
        className="flex-1 px-5 pb-10 max-w-2xl w-full mx-auto space-y-4"
        aria-label="UltraSonic 2025 · archief (gereconstrueerd uit TaskFlow)"
      >
        <div>
          <h1
            className="text-zinc-200 text-lg font-semibold tracking-tight mb-1"
            style={{ letterSpacing: "-0.015em" }}
          >
            UltraSonic 2025 · archief (gereconstrueerd uit TaskFlow)
          </h1>
          <p className="text-zinc-600 text-sm mb-0">
            12 juli 2025 · {archief.locatie}
          </p>
        </div>

        {/* Reconstructie-notitie */}
        <div
          className="rounded-xl px-3.5 py-3"
          style={{
            background: "rgba(255,200,0,0.04)",
            border: "1px solid rgba(255,200,0,0.12)",
          }}
        >
          <p className="text-zinc-500 text-xs leading-relaxed">
            {archief.meta.herkomst}
          </p>
        </div>

        {/* Metadata */}
        <div
          className="rounded-2xl bg-ink-925 p-4"
          style={{ border: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-600" />
            <span className="text-2xs tracking-eyebrow uppercase text-zinc-500">
              CPC Logboek · 12 jul 2025
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3 pb-3 mb-3 border-b border-white/[0.05]">
            <div>
              <div className="text-2xs tracking-eyebrow uppercase text-zinc-500">
                Event
              </div>
              <div className="text-zinc-100 text-sm font-medium mt-0.5 leading-tight">
                {archief.evenement}
              </div>
            </div>
            <div>
              <div className="text-2xs tracking-eyebrow uppercase text-zinc-500">
                Opdrachtgever
              </div>
              <div className="text-zinc-100 text-sm font-medium mt-0.5 leading-tight">
                {archief.opdrachtgever}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 pb-3 mb-3 border-b border-white/[0.05]">
            {archief.cp_team.map((p) => (
              <div key={p.afkorting}>
                <div className="text-2xs tracking-eyebrow uppercase text-zinc-500">
                  {p.functie}
                </div>
                <div className="text-zinc-100 text-sm font-medium mt-0.5">
                  {p.naam}
                </div>
                <div className="text-2xs text-zinc-500 mt-0.5 tabular">
                  {p.afkorting}
                </div>
              </div>
            ))}
          </div>

          <p className="text-2xs text-zinc-600 leading-relaxed">
            {archief.noot_afkortingen}
          </p>
        </div>

        {/* Instroomreeks */}
        <div
          className="rounded-2xl bg-ink-925 p-4"
          style={{ border: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-600" />
            <span className="text-2xs tracking-eyebrow uppercase text-zinc-500">
              Instroomreeks 2025
            </span>
          </div>
          <ol className="space-y-1.5">
            {archief.instroom_reeks.map((punt, i) => (
              <li key={i} className="flex items-baseline justify-between gap-2">
                <span className="text-accent text-2xs tabular font-semibold w-12 shrink-0">
                  {punt.tijd}
                </span>
                <span className="text-zinc-200 text-sm font-medium tabular flex-1">
                  {punt.count.toLocaleString("nl-NL")} bezoekers
                </span>
                {punt.toelichting && (
                  <span className="text-zinc-600 text-2xs shrink-0">
                    {punt.toelichting}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>

        {/* Meldingen-lijst header */}
        <div className="flex items-baseline justify-between px-1 pt-1">
          <h3 className="text-2xs tracking-eyebrow uppercase font-semibold text-zinc-300">
            Meldingen
          </h3>
          <span className="text-2xs text-zinc-500 tabular">
            {total} · {tijdspan}
          </span>
        </div>

        {/* Meldingen */}
        <ol className="space-y-1.5">
          {archief.meldingen.map((m, i) => (
            <li
              key={`${m.tijd}-${i}`}
              className="rounded-xl bg-ink-925 px-3 py-2.5"
              style={{ border: "1px solid rgba(255,255,255,0.04)" }}
            >
              <div className="flex items-start gap-3">
                <div className="text-accent text-2xs tabular font-semibold tracking-tight pt-0.5 w-12 shrink-0">
                  {m.tijd}
                </div>
                <div className="flex-1 min-w-0">
                  {m.melder && (
                    <div className="text-2xs tracking-eyebrow uppercase text-zinc-500 mb-0.5">
                      {m.melder}
                    </div>
                  )}
                  <div className="text-zinc-100 text-sm leading-snug">
                    {m.bericht}
                  </div>
                  {(m.uitgezet_naar || m.afhandeling) && (
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                      {m.uitgezet_naar && (
                        <span className="text-2xs text-zinc-500">
                          <span className="text-zinc-600">→ </span>
                          {m.uitgezet_naar}
                        </span>
                      )}
                      {m.afhandeling && (
                        <span className="text-2xs text-zinc-500">
                          <span className="text-zinc-600">✓ </span>
                          {m.afhandeling}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </main>
    </div>
  );
}
